/** Drei kleine Schaubilder aus Waben - eines je Diskussionsthema, statt Symbolen.
 *  Bewegung per CSS (Klassen sb-* in index.css), mit reduzierter Bewegung stehen sie still. */

const LINIE = 'rgba(200,245,238,.42)'
const FLAECHE = 'rgba(34,195,172,.10)'
const AKZENT = '#22C3AC'
const HELL = '#DFFBF6'

/** Eckpunkte eines Sechsecks: spitz (Spitze oben) oder flach (Kante oben) */
function hex(cx: number, cy: number, r: number, flach = false) {
  return Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 180) * (60 * k + (flach ? 0 : -90))
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`
  }).join(' ')
}

const Rahmen = ({ children }: { children: React.ReactNode }) => (
  <svg viewBox="0 0 320 180" className="h-full w-full" aria-hidden="true">{children}</svg>
)

/** Starke Netzwerke: ein Wabenverbund, an den ein neuer Partner andockt. */
export function SchaubildNetz() {
  const c = { x: 137, y: 90 }
  const d = 45 // Abstand benachbarter Wabenmitten
  const nachbarn = [0, 60, 120, 180, 240, 300].map(g => ({
    x: c.x + d * Math.cos((g * Math.PI) / 180),
    y: c.y + d * Math.sin((g * Math.PI) / 180),
  }))
  const neu = { x: c.x + 2 * d, y: c.y }
  const alle = [c, ...nachbarn]
  return (
    <Rahmen>
      {alle.map((p, i) => <polygon key={i} points={hex(p.x, p.y, 23)} fill={FLAECHE} stroke={LINIE} strokeWidth="1.2" />)}
      <g stroke={AKZENT} strokeWidth="1.2" opacity=".55">
        {nachbarn.map((p, i) => <line key={i} x1={c.x} y1={c.y} x2={p.x} y2={p.y} />)}
        {nachbarn.map((p, i) => {
          const q = nachbarn[(i + 1) % 6]
          return <line key={`r${i}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} />
        })}
      </g>
      <line className="sb-verbinden" x1={nachbarn[0].x} y1={c.y} x2={neu.x} y2={neu.y} stroke={AKZENT} strokeWidth="1.8" pathLength={1} />
      <g className="sb-andocken">
        <polygon points={hex(neu.x, neu.y, 23)} fill="rgba(34,195,172,.24)" stroke={AKZENT} strokeWidth="1.6" />
        <circle cx={neu.x} cy={neu.y} r="4.5" fill={AKZENT} />
      </g>
      {alle.map((p, i) => (
        <circle key={i} className="sb-puls" style={{ animationDelay: `${i * 0.35}s` }} cx={p.x} cy={p.y} r={i === 0 ? 5 : 3.5} fill={HELL} />
      ))}
    </Rahmen>
  )
}

/** Resiliente Lieferketten: ein Impuls laeuft durch die Kette, darunter eine Ausweichroute. */
export function SchaubildKette() {
  const p = Array.from({ length: 6 }, (_, i) => ({ x: 40 + i * 48, y: i % 2 ? 108 : 66 }))
  const pfad = p.map((q, i) => `${i ? 'L' : 'M'}${q.x} ${q.y}`).join(' ')
  const umweg = `M${p[1].x} ${p[1].y} C ${p[1].x + 14} 172, ${p[3].x - 14} 172, ${p[3].x} ${p[3].y}`
  return (
    <Rahmen>
      <path d={umweg} fill="none" stroke={LINIE} strokeWidth="1.3" strokeDasharray="4 5" />
      <path d={pfad} fill="none" stroke={LINIE} strokeWidth="1.3" />
      {p.map((q, i) => <polygon key={i} points={hex(q.x, q.y, 19)} fill="#04293A" stroke={LINIE} strokeWidth="1.2" />)}
      <path d={pfad} className="sb-lauf" pathLength={1} fill="none" stroke={AKZENT} strokeWidth="3" strokeLinecap="round" />
      {p.map((q, i) => (
        // Verzoegerung = Zeitpunkt, zu dem der Impuls diese Wabe erreicht
        <circle key={i} className="sb-melden" style={{ animationDelay: `${(0.32 + 0.54 * i).toFixed(2)}s` }} cx={q.x} cy={q.y} r="4.5" fill={AKZENT} />
      ))}
    </Rahmen>
  )
}

/** Zukunftsfaehige Qualifizierung: Waben wachsen Schicht fuer Schicht zu einer Treppe. */
export function SchaubildQualifizierung() {
  const r = 15
  const hh = Math.sqrt(3) * r // Hoehe einer flachen Wabe
  const hoehen = [1, 2, 2, 3, 4, 5]
  const waben = hoehen.flatMap((n, k) =>
    Array.from({ length: n }, (_, m) => ({
      m, k,
      x: 96 + k * 1.5 * r,
      y: 166 - hh / 2 - m * hh - (k % 2) * (hh / 2),
      oben: m === n - 1,
    })),
  )
  return (
    <Rahmen>
      <line x1="70" y1="166" x2="250" y2="166" stroke={LINIE} strokeWidth="1.2" />
      {waben.map(w => (
        <polygon
          key={`${w.k}-${w.m}`}
          className="sb-wachsen"
          style={{ animationDelay: `${(w.k * 0.16 + w.m * 0.12).toFixed(2)}s` }}
          points={hex(w.x, w.y, r - 1.2, true)}
          fill={w.oben && w.k === hoehen.length - 1 ? AKZENT : `rgba(34,195,172,${(0.1 + w.m * 0.11).toFixed(2)})`}
          stroke={w.oben ? AKZENT : LINIE}
          strokeWidth="1.2"
        />
      ))}
    </Rahmen>
  )
}
